import type { CreateTaskTemplateInput } from '@/lib/validations/task-template';

/**
 * Maps validated template input to the `task_templates` row shape.
 *
 * Lives in its own module so the online mutation path
 * (`use-task-template-mutations.ts`) and the offline replay path
 * (`lib/db/sync-handlers.ts`) share one mapping. When these were two copies, a
 * field added to one would silently be dropped by the other — and the offline
 * copy is the one nobody would notice, because the write happens minutes later
 * with no UI attached to it.
 *
 * Mirrors the server action's own `inputToRow`. Only keys actually present in
 * `input` are emitted, so a partial update never blanks untouched columns.
 */
export function templateInputToRow(
  input: Partial<CreateTaskTemplateInput>,
): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if ('name' in input) row.name = input.name;
  if ('title' in input) row.title = input.title ?? null;
  if ('description' in input) row.description = input.description ?? null;
  if ('category' in input) row.category = input.category ?? null;
  if ('content_type' in input) row.content_type = input.content_type ?? null;
  if ('product_category' in input)
    row.product_category = input.product_category ?? null;
  if ('product_type_other' in input)
    row.product_type_other = input.product_type_other || null;
  if ('word_count_range' in input)
    row.word_count_range = input.word_count_range ?? null;
  if ('research_required' in input)
    row.research_required = input.research_required ?? null;
  if ('brand_voice' in input) row.brand_voice = input.brand_voice ?? null;
  if ('guidelines_url' in input)
    row.guidelines_url = input.guidelines_url || null;
  if ('guidelines_files' in input)
    row.guidelines_files = input.guidelines_files ?? null;
  if ('required_experience_level' in input)
    row.required_experience_level = input.required_experience_level ?? null;
  if ('language' in input) row.language = input.language ?? null;
  if ('amount' in input) row.amount = input.amount ?? null;
  if ('assigned_writer_id' in input)
    row.assigned_writer_id = input.assigned_writer_id ?? null;
  if ('deadline_offset_days' in input)
    row.deadline_offset_days = input.deadline_offset_days ?? null;
  return row;
}
